import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { projectService } from '../../services/projectService';
import { reportService } from '../../services/reportService';
import { billingService } from '../../services/billingService';
import { invoiceService } from '../../services/invoiceService';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ConfirmDialog from '../Common/ConfirmDialog';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Table header styles
const headerCellStyle = {
  backgroundColor: '#7B1FA2',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  borderBottom: '3px solid #4A148C',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
};

const Reports = () => {
  const [projects, setProjects] = useState([]);
  const [monthlyInvoice, setMonthlyInvoice] = useState({
    project_code: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const [monthRange, setMonthRange] = useState({
    project_code: '',
    start_month: '',
    end_month: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [monthRangeData, setMonthRangeData] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [milestoneStatus, setMilestoneStatus] = useState({
    billingGenerated: false,
    milestoneCreated: false,
    locked: false,
  });

  useEffect(() => {
    loadProjects();
  }, []);

  // Check milestone status when project/year/month changes
  useEffect(() => {
    const checkMilestoneStatus = async () => {
      if (monthlyInvoice.project_code && monthlyInvoice.year && monthlyInvoice.month) {
        try {
          const data = await invoiceService.getByProject(monthlyInvoice.project_code);
          const monthStr = `${monthlyInvoice.year}-${String(monthlyInvoice.month).padStart(2, '0')}-01`;
          const monthData = data.invoices?.find((inv) => inv.month === monthStr);

          if (monthData) {
            setMilestoneStatus({
              billingGenerated: monthData.billing_generated,
              milestoneCreated: monthData.milestone_created,
              locked: monthData.locked,
            });
          } else {
            setMilestoneStatus({
              billingGenerated: false,
              milestoneCreated: false,
              locked: false,
            });
          }
        } catch (err) {
          console.error('Failed to check milestone status:', err);
        }
      }
    };

    checkMilestoneStatus();
  }, [monthlyInvoice.project_code, monthlyInvoice.year, monthlyInvoice.month]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (err) {
      setError('Failed to load projects');
    }
  };

  const handleMonthlyInvoiceChange = (e) => {
    const { name, value } = e.target;
    setMonthlyInvoice((prev) => ({ ...prev, [name]: value }));
  };

  const handleMonthRangeChange = (e) => {
    const { name, value } = e.target;
    setMonthRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerateBilling = async () => {
    setError('');
    setSuccess('');

    if (!monthlyInvoice.project_code || !monthlyInvoice.year || !monthlyInvoice.month) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const result = await billingService.generateBilling(
        monthlyInvoice.project_code,
        monthlyInvoice.year,
        monthlyInvoice.month
      );
      setSuccess(`Billing generated successfully! ${result.count} billing records created.`);
      // Update milestone status after generating billing
      setMilestoneStatus((prev) => ({ ...prev, billingGenerated: true }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate billing');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBilling = async () => {
    setError('');
    setSuccess('');

    if (!monthlyInvoice.project_code || !monthlyInvoice.year || !monthlyInvoice.month) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const monthStr = `${monthlyInvoice.year}-${String(monthlyInvoice.month).padStart(2, '0')}-01`;
      await invoiceService.deleteBilling(monthlyInvoice.project_code, monthStr);
      setSuccess('Billing deleted successfully! Invoice section toggle has been reset.');
      // Update milestone status after deleting billing
      setMilestoneStatus({
        billingGenerated: false,
        milestoneCreated: false,
        locked: false,
      });
      // Clear invoice data if displayed
      setInvoiceData(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete billing');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  // Helper functions for delete button
  const isDeleteDisabled = () => {
    // Disabled if no project/month selected
    if (!monthlyInvoice.project_code || !monthlyInvoice.year || !monthlyInvoice.month) {
      return true;
    }
    // Disabled if no billing generated, or milestone created, or locked
    return !milestoneStatus.billingGenerated || milestoneStatus.milestoneCreated || milestoneStatus.locked;
  };

  const getDeleteTooltip = () => {
    if (!monthlyInvoice.project_code || !monthlyInvoice.year || !monthlyInvoice.month) {
      return 'Select project, year and month first';
    }
    if (!milestoneStatus.billingGenerated) {
      return 'No billing to delete - generate billing first';
    }
    if (milestoneStatus.milestoneCreated) {
      return 'Cannot delete - milestone already created';
    }
    if (milestoneStatus.locked) {
      return 'Cannot delete - billing is locked/approved';
    }
    return 'Delete billing records';
  };

  const handleGenerateMonthlyInvoice = async (format) => {
    setError('');
    setSuccess('');

    if (!monthlyInvoice.project_code || !monthlyInvoice.year || !monthlyInvoice.month) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const data = await reportService.generateMonthlyInvoice(
        monthlyInvoice.project_code,
        monthlyInvoice.year,
        monthlyInvoice.month,
        format
      );

      if (format === 'json') {
        setInvoiceData(data);
        setSuccess('Invoice data retrieved successfully');
      } else {
        setSuccess(`Invoice ${format.toUpperCase()} downloaded successfully`);
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMonthRange = async () => {
    setError('');
    setSuccess('');

    if (!monthRange.project_code || !monthRange.start_month || !monthRange.end_month) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const data = await reportService.generateMonthRangeReport(
        monthRange.project_code,
        monthRange.start_month,
        monthRange.end_month
      );
      console.log('Month Range Report Data:', data);
      setMonthRangeData(data);
      setSuccess('Month range report generated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Month range error:', err);
      setError(err.response?.data?.error || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Export to colorful Excel
  const exportToExcel = () => {
    if (!invoiceData || !invoiceData.billings) return;

    const monthName = months.find(m => m.value === monthlyInvoice.month)?.label || '';
    const projectName = invoiceData.project?.name || monthlyInvoice.project_code;
    const projectCode = invoiceData.project?.project_code || monthlyInvoice.project_code;

    // Header info rows (Project Name, Project Code, Invoice Month)
    const infoRows = [
      ['Project Name:', projectName],
      ['Project Code:', projectCode],
      ['Invoice Month:', `${monthName} ${monthlyInvoice.year}`],
      [], // Empty row for spacing
    ];

    const headers = ['SNo', 'Employee ID', 'Employee Name', 'Location', 'Bill Rate', 'Billable Days', 'Billable Hours', 'Base Value', 'Discount', 'Final Value'];

    // Create data rows
    const dataRows = invoiceData.billings.map((billing, index) => [
      index + 1,
      billing.emp_id_display || 'N/A',
      billing.name || 'N/A',
      billing.location_name || 'N/A',
      parseFloat(billing.bill_rate || 0),
      billing.billable_days || 0,
      billing.billable_hours || 0,
      parseFloat(billing.base_billable_value || 0),
      parseFloat(billing.discount_value || 0),
      parseFloat(billing.billable_value || 0),
    ]);

    // Add totals row
    const totalsRow = [
      '', 'TOTAL', '', '', '',
      invoiceData.totals?.totalDays || 0,
      invoiceData.totals?.totalHours || 0,
      invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.base_billable_value || 0), 0),
      invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.discount_value || 0), 0),
      invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.billable_value || 0), 0),
    ];

    const wsData = [...infoRows, headers, ...dataRows, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Style info rows (Project Name, Project Code, Invoice Month)
    const infoRowCount = infoRows.length;
    for (let i = 0; i < 3; i++) {
      // Label cell (column 0)
      const labelRef = XLSX.utils.encode_cell({ r: i, c: 0 });
      if (ws[labelRef]) {
        ws[labelRef].s = {
          font: { bold: true, sz: 12, color: { rgb: '7B1FA2' } },
          alignment: { horizontal: 'left', vertical: 'center' },
        };
      }
      // Value cell (column 1)
      const valueRef = XLSX.utils.encode_cell({ r: i, c: 1 });
      if (ws[valueRef]) {
        ws[valueRef].s = {
          font: { bold: true, sz: 12, color: { rgb: '000000' } },
          alignment: { horizontal: 'left', vertical: 'center' },
        };
      }
    }

    // Style table headers (now at row index = infoRowCount)
    const headerRowIndex = infoRowCount;
    headers.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: index });
      let bgColor = '7B1FA2'; // Purple default
      if (index === 4) bgColor = '1565C0'; // Blue for Bill Rate
      else if (index === 7) bgColor = '2E7D32'; // Green for Base Value
      else if (index === 8) bgColor = 'E65100'; // Orange for Discount
      else if (index === 9) bgColor = '1565C0'; // Blue for Final Value

      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: bgColor } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: {
            bottom: { style: 'medium', color: { rgb: '000000' } },
          },
        };
      }
    });

    // Style data rows with alternating colors
    dataRows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex + 1 + rowIndex, c: colIndex });
        if (ws[cellRef]) {
          const isNumber = colIndex >= 4;
          ws[cellRef].s = {
            fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'F3E5F5' } },
            alignment: { horizontal: isNumber ? 'right' : (colIndex === 0 ? 'center' : 'left'), vertical: 'center' },
            numFmt: isNumber && colIndex >= 7 ? '$#,##0.00' : undefined,
          };
        }
      });
    });

    // Style totals row
    const totalsRowIndex = headerRowIndex + 1 + dataRows.length;
    totalsRow.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: totalsRowIndex, c: colIndex });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: 'E1BEE7' } },
          font: { bold: true, sz: 11 },
          alignment: { horizontal: colIndex >= 4 ? 'right' : 'left', vertical: 'center' },
          numFmt: colIndex >= 7 ? '$#,##0.00' : undefined,
          border: {
            top: { style: 'medium', color: { rgb: '7B1FA2' } },
          },
        };
      }
    });

    // Set column widths
    ws['!cols'] = [
      { wch: 6 }, { wch: 14 }, { wch: 22 }, { wch: 15 }, { wch: 12 },
      { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice');

    XLSX.writeFile(wb, `Invoice_${monthlyInvoice.project_code}_${monthName}_${monthlyInvoice.year}.xlsx`);
    setSuccess('Excel file downloaded successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Export to colorful PDF
  const exportToPDF = () => {
    if (!invoiceData || !invoiceData.billings) return;

    const doc = new jsPDF('landscape');
    const monthName = months.find(m => m.value === monthlyInvoice.month)?.label || '';
    const projectName = invoiceData.project?.name || monthlyInvoice.project_code;
    const projectCode = invoiceData.project?.project_code || monthlyInvoice.project_code;

    // Header info (Project Name, Project Code, Invoice Month)
    let yPos = 15;

    // Project Name
    doc.setFontSize(11);
    doc.setTextColor(123, 31, 162);
    doc.setFont(undefined, 'bold');
    doc.text('Project Name:', 14, yPos);
    doc.setTextColor(0, 0, 0);
    doc.text(projectName, 55, yPos);

    // Project Code
    yPos += 7;
    doc.setTextColor(123, 31, 162);
    doc.text('Project Code:', 14, yPos);
    doc.setTextColor(0, 0, 0);
    doc.text(projectCode, 55, yPos);

    // Invoice Month
    yPos += 7;
    doc.setTextColor(123, 31, 162);
    doc.text('Invoice Month:', 14, yPos);
    doc.setTextColor(0, 0, 0);
    doc.text(`${monthName} ${monthlyInvoice.year}`, 55, yPos);

    // Table headers
    const headers = [['SNo', 'Employee ID', 'Employee Name', 'Location', 'Bill Rate', 'Days', 'Hours', 'Base Value', 'Discount', 'Final Value']];

    // Table data
    const data = invoiceData.billings.map((billing, index) => [
      index + 1,
      billing.emp_id_display || 'N/A',
      billing.name || 'N/A',
      billing.location_name || 'N/A',
      `$${parseFloat(billing.bill_rate || 0).toFixed(2)}`,
      billing.billable_days || 0,
      billing.billable_hours || 0,
      `$${parseFloat(billing.base_billable_value || 0).toFixed(2)}`,
      `-$${parseFloat(billing.discount_value || 0).toFixed(2)}`,
      `$${parseFloat(billing.billable_value || 0).toFixed(2)}`,
    ]);

    // Add totals row
    data.push([
      '', 'TOTAL', '', '', '',
      invoiceData.totals?.totalDays || 0,
      invoiceData.totals?.totalHours || 0,
      `$${invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.base_billable_value || 0), 0).toFixed(2)}`,
      `-$${invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.discount_value || 0), 0).toFixed(2)}`,
      `$${invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.billable_value || 0), 0).toFixed(2)}`,
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 38,
      theme: 'grid',
      headStyles: {
        fillColor: [123, 31, 162],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { halign: 'left' },
        4: { halign: 'right', fillColor: [227, 242, 253] },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'right', fillColor: [232, 245, 233] },
        8: { halign: 'right', fillColor: [255, 243, 224] },
        9: { halign: 'right', fontStyle: 'bold', fillColor: [227, 242, 253] },
      },
      alternateRowStyles: {
        fillColor: [243, 229, 245],
      },
      footStyles: {
        fillColor: [225, 190, 231],
        fontStyle: 'bold',
      },
      didParseCell: (data) => {
        // Style the last row (totals) differently
        if (data.row.index === invoiceData.billings.length) {
          data.cell.styles.fillColor = [225, 190, 231];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, pageHeight - 10);
    doc.text(`Total Billable Value: $${invoiceData.totals?.totalValue?.toFixed(2) || '0.00'}`, 200, pageHeight - 10);

    doc.save(`Invoice_${monthlyInvoice.project_code}_${monthName}_${monthlyInvoice.year}.pdf`);
    setSuccess('PDF file downloaded successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  return (
    <Container maxWidth="lg">
      {/* Page Header */}
      <Box sx={{
        mb: 3,
        mt: 2,
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #7B1FA2 0%, #AB47BC 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(123, 31, 162, 0.3)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AssessmentIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Billing Reports</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Generate monthly invoices and billing reports
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Monthly Invoice Report */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <AssessmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Monthly Invoice
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Generate invoice for a single month with detailed breakdown per employee
                </Typography>
                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth
                  select
                  label="Project"
                  name="project_code"
                  value={monthlyInvoice.project_code}
                  onChange={handleMonthlyInvoiceChange}
                  margin="normal"
                  size="small"
                >
                  {projects.map((project) => (
                    <MenuItem key={project.project_code} value={project.project_code}>
                      {project.name} ({project.project_code})
                    </MenuItem>
                  ))}
                </TextField>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      label="Year"
                      name="year"
                      value={monthlyInvoice.year}
                      onChange={handleMonthlyInvoiceChange}
                      margin="normal"
                      size="small"
                    >
                      {years.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      label="Month"
                      name="month"
                      value={monthlyInvoice.month}
                      onChange={handleMonthlyInvoiceChange}
                      margin="normal"
                      size="small"
                    >
                      {months.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                {/* Billing Status Indicator */}
                {monthlyInvoice.project_code && monthlyInvoice.year && monthlyInvoice.month && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: milestoneStatus.billingGenerated ? 'success.50' : 'grey.100', borderRadius: 1, border: '1px solid', borderColor: milestoneStatus.billingGenerated ? 'success.main' : 'grey.300' }}>
                    <Typography variant="body2" color={milestoneStatus.billingGenerated ? 'success.main' : 'text.secondary'}>
                      <strong>Status:</strong>{' '}
                      {milestoneStatus.billingGenerated
                        ? milestoneStatus.locked
                          ? '✅ Billing generated & Locked (Approved)'
                          : milestoneStatus.milestoneCreated
                            ? '✅ Billing generated & Milestone created'
                            : '✅ Billing generated (can be deleted)'
                        : '⚪ No billing generated for this month'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CalculateIcon />}
                    onClick={handleGenerateBilling}
                    disabled={loading}
                    color="primary"
                  >
                    Generate Billing
                  </Button>
                  <Tooltip title={getDeleteTooltip()} arrow>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteDialogOpen(true)}
                        disabled={loading || isDeleteDisabled()}
                        color="error"
                      >
                        Delete Billing
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleGenerateMonthlyInvoice('json')}
                    disabled={loading}
                  >
                    View Data
                  </Button>
                  <Tooltip title={invoiceData ? "Download colorful PDF" : "Load data first with 'View Data'"} arrow>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={exportToPDF}
                        disabled={loading || !invoiceData}
                        color="error"
                      >
                        PDF
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={invoiceData ? "Download colorful Excel" : "Load data first with 'View Data'"} arrow>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<TableChartIcon />}
                        onClick={exportToExcel}
                        disabled={loading || !invoiceData}
                        color="success"
                      >
                        Excel
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </CardActions>
            </Card>
          </Grid>

          {/* Month Range Report */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <AssessmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Month Range Report
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Generate consolidated report for multiple months
                </Typography>
                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth
                  select
                  label="Project"
                  name="project_code"
                  value={monthRange.project_code}
                  onChange={handleMonthRangeChange}
                  margin="normal"
                  size="small"
                >
                  {projects.map((project) => (
                    <MenuItem key={project.project_code} value={project.project_code}>
                      {project.name} ({project.project_code})
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  label="Start Month"
                  name="start_month"
                  type="month"
                  value={monthRange.start_month}
                  onChange={handleMonthRangeChange}
                  margin="normal"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                <TextField
                  fullWidth
                  label="End Month"
                  name="end_month"
                  type="month"
                  value={monthRange.end_month}
                  onChange={handleMonthRangeChange}
                  margin="normal"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleGenerateMonthRange}
                  disabled={loading}
                  startIcon={<AssessmentIcon />}
                >
                  Generate Report
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        {/* Invoice Data Display */}
        {invoiceData && (
          <Box sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Invoice Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Project Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Project
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {invoiceData.project?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Project Code
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {invoiceData.project?.project_code || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Month
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(invoiceData.year, invoiceData.month - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Billable Value
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${invoiceData.totals?.totalValue?.toFixed(2) || '0.00'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Employee Details Table */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Employee Billing Breakdown
              </Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellStyle, width: 60 }} align="center">SNo</TableCell>
                      <TableCell sx={headerCellStyle}>Employee ID</TableCell>
                      <TableCell sx={headerCellStyle}>Employee Name</TableCell>
                      <TableCell sx={headerCellStyle}>Location</TableCell>
                      <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1565C0', borderBottom: '3px solid #0D47A1' }} align="right">Bill Rate</TableCell>
                      <TableCell sx={headerCellStyle} align="right">Billable Days</TableCell>
                      <TableCell sx={headerCellStyle} align="right">Billable Hours</TableCell>
                      <TableCell sx={{ ...headerCellStyle, backgroundColor: '#2E7D32', borderBottom: '3px solid #1B5E20' }} align="right">Base Value</TableCell>
                      <TableCell sx={{ ...headerCellStyle, backgroundColor: '#E65100', borderBottom: '3px solid #BF360C' }} align="right">Discount</TableCell>
                      <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1565C0', borderBottom: '3px solid #0D47A1' }} align="right">Final Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceData.billings && invoiceData.billings.length > 0 ? (
                      <>
                        {invoiceData.billings.map((billing, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#F3E5F5',
                              '&:hover': { backgroundColor: '#E1BEE7' },
                              transition: 'background-color 0.2s ease',
                            }}
                          >
                            <TableCell align="center">{index + 1}</TableCell>
                            <TableCell>{billing.emp_id_display || 'N/A'}</TableCell>
                            <TableCell>{billing.name || 'N/A'}</TableCell>
                            <TableCell>{billing.location_name || 'N/A'}</TableCell>
                            <TableCell align="right">${parseFloat(billing.bill_rate || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{billing.billable_days || 0}</TableCell>
                            <TableCell align="right">{billing.billable_hours || 0}</TableCell>
                            <TableCell align="right">
                              ${parseFloat(billing.base_billable_value || 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                              -${parseFloat(billing.discount_value || 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              ${parseFloat(billing.billable_value || 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Subtotal Row */}
                        <TableRow sx={{ backgroundColor: 'grey.200', '&:hover': { backgroundColor: 'grey.200' } }}>
                          <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                            Subtotal:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                            ${invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.base_billable_value || 0), 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'error.main' }}>
                            -${invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.discount_value || 0), 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'primary.main' }}>
                            ${invoiceData.billings.reduce((sum, b) => sum + parseFloat(b.billable_value || 0), 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No employee billing data available for this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary Section */}
              {invoiceData.totals && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Total Employees
                      </Typography>
                      <Typography variant="h6">
                        {invoiceData.billings?.length || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Total Billable Hours
                      </Typography>
                      <Typography variant="h6">
                        {invoiceData.totals.totalHours || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Total Billable Days
                      </Typography>
                      <Typography variant="h6">
                        {invoiceData.totals.totalDays || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Month Range Report Data Display */}
        {monthRangeData && (
          <Box sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Month Range Report
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Project Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Project
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {monthRangeData.project?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Project Code
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {monthRangeData.project?.project_code || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Months
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {monthRangeData.monthlyTotals?.length || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Grand Total Value
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${monthRangeData.grandTotals?.totalValue?.toFixed(2) || '0.00'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Monthly Breakdown */}
              {monthRangeData.monthlyTotals && monthRangeData.monthlyTotals.length > 0 ? (
                monthRangeData.monthlyTotals.map((monthData, monthIndex) => (
                  <Box key={monthIndex} sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      {new Date(monthData.month).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                    </Typography>
                    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={headerCellStyle}>Employee ID</TableCell>
                            <TableCell sx={headerCellStyle}>Name</TableCell>
                            <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1565C0', borderBottom: '3px solid #0D47A1' }} align="right">Bill Rate</TableCell>
                            <TableCell sx={headerCellStyle} align="right">Days</TableCell>
                            <TableCell sx={headerCellStyle} align="right">Hours</TableCell>
                            <TableCell sx={{ ...headerCellStyle, backgroundColor: '#2E7D32', borderBottom: '3px solid #1B5E20' }} align="right">Base Value</TableCell>
                            <TableCell sx={{ ...headerCellStyle, backgroundColor: '#E65100', borderBottom: '3px solid #BF360C' }} align="right">Discount</TableCell>
                            <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1565C0', borderBottom: '3px solid #0D47A1' }} align="right">Final Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthData.records.map((billing, idx) => (
                            <TableRow
                              key={idx}
                              sx={{
                                backgroundColor: idx % 2 === 0 ? '#ffffff' : '#F3E5F5',
                                '&:hover': { backgroundColor: '#E1BEE7' },
                                transition: 'background-color 0.2s ease',
                              }}
                            >
                              <TableCell>{billing.emp_id_display || 'N/A'}</TableCell>
                              <TableCell>{billing.name || 'N/A'}</TableCell>
                              <TableCell align="right">${parseFloat(billing.bill_rate || 0).toFixed(2)}</TableCell>
                              <TableCell align="right">{billing.billable_days || 0}</TableCell>
                              <TableCell align="right">{billing.billable_hours || 0}</TableCell>
                              <TableCell align="right">
                                ${parseFloat(billing.base_billable_value || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>
                                -${parseFloat(billing.discount_value || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                ${parseFloat(billing.billable_value || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ backgroundColor: 'grey.200', '&:hover': { backgroundColor: 'grey.200' } }}>
                            <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                              Subtotal:
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{monthData.totals.totalDays}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{monthData.totals.totalHours}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                              ${monthData.records.reduce((sum, b) => sum + parseFloat(b.base_billable_value || 0), 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'error.main' }}>
                              -${monthData.records.reduce((sum, b) => sum + parseFloat(b.discount_value || 0), 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'primary.main' }}>
                              ${monthData.records.reduce((sum, b) => sum + parseFloat(b.billable_value || 0), 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))
              ) : (
                <Typography variant="body1" color="textSecondary">
                  No monthly data available
                </Typography>
              )}

              {/* Grand Totals */}
              {monthRangeData.grandTotals && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'primary.light', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" sx={{ color: 'white' }}>
                        Grand Total Days
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {monthRangeData.grandTotals.totalDays || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" sx={{ color: 'white' }}>
                        Grand Total Hours
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {monthRangeData.grandTotals.totalHours || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" sx={{ color: 'white' }}>
                        Grand Total Value
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        ${monthRangeData.grandTotals.totalValue.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Box>
        )}

      {/* Delete Billing Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteBilling}
        title="Delete Billing Records"
        message="Are you sure you want to delete all billing records for this month? This will also reset the Generate Billing toggle in the Invoice section."
        details={[
          { label: 'Project', value: monthlyInvoice.project_code },
          { label: 'Month', value: `${months.find(m => m.value === monthlyInvoice.month)?.label || ''} ${monthlyInvoice.year}` },
        ]}
        confirmText="Delete"
        confirmColor="error"
      />
      </Box>
    </Container>
  );
};

export default Reports;
